import axios from 'axios';

// Determine API base URL based on environment
// In development, use the proxy (relative URL)
// In production, set REACT_APP_API_URL during build
const getBaseURL = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    return apiUrl;
  }
  return '/api';
};

const apiService = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authorization header to all requests
apiService.interceptors.request.use(
  (config) => {
    // Prefer kid token if available, otherwise use parent token
    const kidToken = localStorage.getItem('kidToken');
    const parentToken = localStorage.getItem('parentToken');

    if (kidToken) {
      config.headers.Authorization = `Bearer ${kidToken}`;
    } else if (parentToken) {
      config.headers.Authorization = `Bearer ${parentToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses by dispatching a logout event
// The AuthContext listens for this and performs React Router navigation
apiService.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear tokens
      localStorage.removeItem('parentToken');
      localStorage.removeItem('kidToken');
      localStorage.removeItem('selectedKidId');

      // Dispatch event so AuthContext can handle navigation via React Router
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  googleLogin: (idToken: string) => apiService.post('/auth/google', { idToken }),
  devLogin: () => apiService.post('/auth/dev-login'),
  getMe: (token: string) => apiService.get('/auth/me', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  kidLogin: (username: string, pinCode: string) => apiService.post('/auth/kid/login', { username, pinCode }),
  getKidMe: (token: string) => apiService.get('/auth/kid/me', {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

// Targets API
export const targetApi = {
  getAll: (userId: string) => apiService.get(`/targets/user/${userId}`),
  getById: (id: string) => apiService.get(`/targets/${id}`),
  create: (target: any) => apiService.post('/targets', target),
  update: (id: string, target: any) => apiService.put(`/targets/${id}`, target),
  markCompleted: (id: string) => apiService.patch(`/targets/${id}/complete`),
  delete: (id: string) => apiService.delete(`/targets/${id}`),
  getTemplates: () => apiService.get('/targets/templates'),
};

// Gifts API
export const giftApi = {
  getAll: () => apiService.get('/gifts'),
  getById: (id: string) => apiService.get(`/gifts/${id}`),
  create: (gift: any) => apiService.post('/gifts', gift),
  update: (id: string, gift: any) => apiService.put(`/gifts/${id}`, gift),
  delete: (id: string) => apiService.delete(`/gifts/${id}`),
};

// Redemption API
export const redemptionApi = {
  redeemGift: (giftId: string, userId: string) => apiService.post('/redemptions', { giftId, userId }),
  getRedemptions: (userId: string) => apiService.get(`/redemptions/user/${userId}`),
};

// User API
export const userApi = {
  getAll: () => apiService.get('/users'),
  getById: (id: string) => apiService.get(`/users/${id}`),
  create: (user: any) => apiService.post('/users', user),
  delete: (id: string) => apiService.delete(`/users/${id}`),
  setPin: (id: string, pinCode: string) => apiService.post(`/users/${id}/set-pin`, { pinCode }),
  generatePin: (id: string) => apiService.post(`/users/${id}/generate-pin`),
  getLoginAttempts: (id: string, limit: number = 20) => apiService.get(`/users/${id}/login-attempts?limit=${limit}`),
};

// Statistics API
export const statisticsApi = {
  getForUser: (userId: string) => apiService.get(`/statistics/user/${userId}`),
};

// Badges API (deprecated, use energyApi)
export const badgeApi = {
  getUserBadges: (userId: string) => apiService.get(`/badges/user/${userId}`),
};

// Energy API
export const energyApi = {
  getUserEnergy: (userId: string) => apiService.get(`/energy/user/${userId}`),
};

// Family API
export const familyApi = {
  getFamily: () => apiService.get('/family'),
  getMembers: () => apiService.get('/family/members'),
  createInvite: () => apiService.post('/family/invite'),
  joinFamily: (inviteCode: string) => apiService.post(`/family/join/${inviteCode}`),
  getInvitations: () => apiService.get('/family/invitations'),
  cancelInvitation: (id: string) => apiService.delete(`/family/invitations/${id}`),
  validateInviteCode: (inviteCode: string) => apiService.get(`/family/validate/${inviteCode}`),
};

// Friends API
export const friendsApi = {
  createInvitation: (kidId: string) => apiService.post(`/friends/invitation?kidId=${kidId}`),
  getInvitationByCode: (code: string) => apiService.get(`/friends/invitation/${code}`),
  acceptInvitation: (code: string, kidId: string) => apiService.post(`/friends/invitation/${code}/accept?kidId=${kidId}`),
  getPendingRequests: (kidId: string) => apiService.get(`/friends/requests?kidId=${kidId}`),
  getSentRequests: (kidId: string) => apiService.get(`/friends/requests/sent?kidId=${kidId}`),
  confirmRequest: (requestId: string, kidId: string) => apiService.post(`/friends/requests/${requestId}/confirm?kidId=${kidId}`),
  declineRequest: (requestId: string, kidId: string) => apiService.post(`/friends/requests/${requestId}/decline?kidId=${kidId}`),
  getFriends: (kidId: string) => apiService.get(`/friends?kidId=${kidId}`),
  removeFriend: (kidId: string, friendId: string) => apiService.delete(`/friends/${friendId}?kidId=${kidId}`),
  getActiveInvitations: (kidId: string) => apiService.get(`/friends/invitations?kidId=${kidId}`),
};

export default apiService;